import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";

const AUTO_SCROLL_MS = 3800;
const TRANSITION_S = 0.5; // 500ms — within the 400-600ms range
const ACCENT = "#940835";

const cardVariants = {
  enter: (direction) => ({
    opacity: 0,
    x: direction >= 0 ? 56 : -56,
    y: 18,
    scale: 0.98,
  }),
  center: {
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
  },
  exit: (direction) => ({
    opacity: 0,
    x: direction >= 0 ? -56 : 56,
    y: -12,
    scale: 0.98,
  }),
};

export default function Testimonials() {
  const [[idx, direction], setState] = useState([0, 1]);
  const [isPaused, setIsPaused] = useState(false);
  const total = TESTIMONIALS.length;
  const t = TESTIMONIALS[idx];

  const go = useCallback(
    (dir) => {
      setState(([current]) => [(current + dir + total) % total, dir]);
    },
    [total]
  );

  const goTo = useCallback((target) => {
    setState(([current]) => [target, target >= current ? 1 : -1]);
  }, []);

  // Auto-scroll: advances every AUTO_SCROLL_MS, paused on hover, resets whenever
  // the user manually navigates so it doesn't jump right after an interaction.
  useEffect(() => {
    if (isPaused || total <= 1) return undefined;

    const timer = setTimeout(() => {
      go(1);
    }, AUTO_SCROLL_MS);

    return () => clearTimeout(timer);
  }, [idx, isPaused, go, total]);

  // Swipe support for mobile: drag the active card left/right.
  const handleDragEnd = (_, info) => {
    const swipeThreshold = 60;
    if (info.offset.x <= -swipeThreshold || info.velocity.x <= -400) {
      go(1);
    } else if (info.offset.x >= swipeThreshold || info.velocity.x >= 400) {
      go(-1);
    }
  };

  return (
    <section
      id="stories"
      data-testid="stories-section"
      className="section"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="container-x">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#0A3161]/15 bg-[#0A3161]/5 px-3 py-1 text-[12px] font-semibold text-[#0A3161]">
              Success Stories
            </div>
            <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-slate-900" data-testid="stories-heading">
              Real students. <span className="text-[#B31942]">Real offers.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="story-prev"
              onClick={() => go(-1)}
              aria-label="Previous review"
              className="h-11 w-11 rounded-full border border-black/10 bg-white flex items-center justify-center hover:border-[#940835]/40 hover:text-[#940835] transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-slate-700" />
            </button>
            <button
              data-testid="story-next"
              onClick={() => go(1)}
              aria-label="Next review"
              className="h-11 w-11 rounded-full bg-[#B31942] text-white flex items-center justify-center hover:bg-[#940835] shadow-[0_10px_24px_rgba(148,8,53,0.32)] transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-6 items-stretch">
          <div className="lg:col-span-8 flex flex-col">
            <div className="relative overflow-hidden rounded-3xl">
              <AnimatePresence mode="wait" custom={direction} initial={false}>
                <motion.div
                  key={idx}
                  custom={direction}
                  variants={cardVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: TRANSITION_S, ease: [0.22, 1, 0.36, 1] }}
                  drag="x"
                  dragElastic={0.15}
                  dragConstraints={{ left: 0, right: 0 }}
                  onDragEnd={handleDragEnd}
                  whileTap={{ cursor: "grabbing" }}
                  className="cursor-grab rounded-3xl bg-white border p-6 md:p-10 touch-pan-y"
                  style={{
                    borderColor: `${ACCENT}33`,
                    boxShadow: `0 20px 45px -18px ${ACCENT}40, 0 2px 8px rgba(0,0,0,0.04)`,
                  }}
                  data-testid="story-active"
                >
                  <Quote className="h-10 w-10" style={{ color: ACCENT }} />
                  <p className="mt-4 text-xl md:text-2xl leading-relaxed text-slate-800 font-medium">
                    “{t.quote}”
                  </p>
                  <div className="mt-6 flex items-center gap-4">
                    <img
                      src={t.image}
                      alt={t.name}
                      className="h-14 w-14 rounded-full object-cover border-2"
                      style={{ borderColor: `${ACCENT}55` }}
                    />
                    <div>
                      <div className="font-semibold text-slate-900">{t.name}</div>
                      <div className="text-sm text-slate-500">{t.course}</div>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-sm font-semibold text-[#0A3161]">{t.company}</div>
                      <div className="text-xs font-semibold" style={{ color: ACCENT }}>
                        {t.pkg}
                      </div>
                      <div className="mt-1 flex items-center gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <Star key={i} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dot indicators */}
            <div className="mt-6 flex items-center justify-center gap-2" data-testid="story-dots">
              {TESTIMONIALS.map((s, i) => (
                <button
                  key={s.name}
                  data-testid={`story-dot-${i}`}
                  onClick={() => goTo(i)}
                  aria-label={`Go to review ${i + 1}`}
                  aria-current={i === idx}
                  className="h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: i === idx ? "28px" : "10px",
                    backgroundColor: i === idx ? ACCENT : "#0000001f",
                  }}
                />
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-3">
            {TESTIMONIALS.map((s, i) => (
              <button
                key={s.name}
                data-testid={`story-thumb-${i}`}
                onClick={() => goTo(i)}
                className="text-left rounded-2xl border p-4 transition-all duration-500"
                style={
                  i === idx
                    ? {
                        backgroundColor: ACCENT,
                        color: "#fff",
                        borderColor: ACCENT,
                        boxShadow: `0 12px 28px -12px ${ACCENT}55`,
                      }
                    : { backgroundColor: "#fff", borderColor: "rgba(0,0,0,0.05)" }
                }
              >
                <div className="flex items-center gap-3">
                  <img src={s.image} alt={s.name} className="h-10 w-10 rounded-full object-cover" />
                  <div>
                    <div className={`font-semibold text-sm ${i === idx ? "text-white" : "text-slate-900"}`}>{s.name}</div>
                    <div className={`text-xs ${i === idx ? "text-white/70" : "text-slate-500"}`}>{s.company} · {s.pkg}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
